// /stacks/traffic/dev/frontend/src/features/sightings/SightingsTable.jsx
import React, { useMemo, useRef } from "react";
import ExportMenu from "../../components/ExportMenu"; // shared menu
import { fmtFullWithDow } from "../../lib/dates";

// Pretty labels for this page
function prettyCamName(k) {
  const s = String(k || "").toLowerCase();
  if (s === "41") return "41";
  if (s === "cheltenham") return "Cheltenham";
  if (s === "pool") return "Pool";
  return String(k || "");
}

// Simple downloader
function download(filename, mime, text) {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function SightingsTable({
  rows,
  total,
  limit,
  offset,
  order,
  onPrev,
  onNext,
  onRowClick,
}) {
  const gridRef = useRef(null);

  // Paging math that tolerates unknown total
  const pageSize = limit || 100;
  const pageOff  = offset || 0;
  const hasRows  = Array.isArray(rows) && rows.length > 0;
  const hasTotal = Number.isFinite(total) && total >= 0;

  const start = hasRows ? pageOff + 1 : 0;
  const end   = pageOff + (rows?.length || 0);

  const disablePrev = pageOff <= 0;
  const disableNext = hasTotal
    ? end >= total
    : (rows?.length || 0) < pageSize
      ? true
      : false;

  // CSV/Excel text for the **current page** (visible rows only)
  const csvText = useMemo(() => {
    const header = "Time,Camera,Plate\n";
    const body = (rows || [])
      .map((r) => `${fmtFullWithDow(r.ts)},${prettyCamName(r.camera)},${r.plate || ""}`)
      .join("\n");
    return header + body;
  }, [rows]);

  const doCSV   = () => download("sightings.csv", "text/csv;charset=utf-8", csvText);
  const doExcel = () => download("sightings.xls", "application/vnd.ms-excel", csvText);

  const doPDF = async () => {
    if (!gridRef.current) return;
    const [{ default: html2canvas }, { default: jsPDF }] = await Promise.all([
      import("html2canvas"),
      import("jspdf"),
    ]);
    const canvas = await html2canvas(gridRef.current, { backgroundColor: "#0b121c", scale: 2 });
    const img = canvas.toDataURL("image/png");
    const pdf = new jsPDF({ orientation: "landscape", unit: "px", format: "a4" });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const margin = 16;
    const imgW = pageW - margin * 2;
    const imgH = (canvas.height / canvas.width) * imgW;
    pdf.addImage(img, "PNG", margin, margin, imgW, Math.min(imgH, pageH - margin * 2));
    pdf.save("sightings.pdf");
  };

  const doPrint = async () => {
    if (!gridRef.current) return;
    const { default: html2canvas } = await import("html2canvas");
    const canvas = await html2canvas(gridRef.current, { backgroundColor: "#0b121c", scale: 2 });
    const dataUrl = canvas.toDataURL("image/png");
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(
      `<html><head><title>Sightings</title></head>
       <body style="margin:0;background:#0b121c;display:flex;justify-content:center">
         <img src="${dataUrl}" style="max-width:100%;height:auto"/>
         <script>window.onload=function(){setTimeout(function(){window.print();},0);}</script>
       </body></html>`
    );
    win.document.close();
  };

  // Shared ExportMenu calls us with one of: 'csv' | 'excel' | 'pdf' | 'print'
  const handleExport = (mode) => {
    switch (mode) {
      case "csv":   return doCSV();
      case "excel": return doExcel();
      case "pdf":   return doPDF();
      case "print": return doPrint();
      default:      return;
    }
  };

  return (
    <section className="card">
      <div
        className="card-header"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 8,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 18 }}>Results</h2>
        <ExportMenu onExport={handleExport} />
      </div>

      <div style={{ fontSize: 12, opacity: 0.8, marginBottom: 8 }}>
        {hasRows
          ? `Showing ${start}-${end}${hasTotal ? ` of ${total}` : ""}`
          : "No results"}
      </div>

      {/* Grid we export/print */}
      <div
        ref={gridRef}
        className="results-grid"
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(520px, 1fr))",
          gap: 10,
        }}
      >
        {(rows || []).map((r, i) => (
          <div
            key={r.detection_id || `${r.ts}-${r.camera}-${r.plate}-${i}`}
            role="button"
            tabIndex={0}
            onClick={() => onRowClick && onRowClick(r)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onRowClick && onRowClick(r);
              }
            }}
            style={{
              display: "grid",
              gridTemplateColumns: "1fr auto auto",
              gap: 8,
              alignItems: "center",
              padding: "10px 12px",
              borderRadius: 8,
              background: "#0b121c",
              border: "1px solid #1a2533",
              cursor: "pointer",
            }}
          >
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontWeight: 600 }}>{fmtFullWithDow(r.ts)}</span>
              <span style={{ opacity: 0.75, fontSize: 12 }}>{prettyCamName(r.camera)}</span>
            </div>

            <div
              style={{
                textAlign: "right",
                fontFamily: "monospace",
                fontSize: 16,
              }}
              title="Plate"
            >
              {r.plate || <span style={{ opacity: 0.6 }}>(no plate)</span>}
            </div>

            <div style={{ textAlign: "right", fontSize: 12, opacity: 0.7 }}>
              {order === "asc" ? "↑" : "↓"}
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 12 }}>
        <button className="btn" onClick={onPrev} disabled={disablePrev}>
          ← Prev
        </button>
        <button className="btn" onClick={onNext} disabled={disableNext}>
          Next →
        </button>
      </div>
    </section>
  );
}
